import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal, Select, Space, Table, Typography, Alert } from 'antd';
import { fetchCronJobHistory } from '../../services/cronLogService';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    return date.toLocaleString('vi-VN', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour12: false,
    });
  } catch (e) {
    return value;
  }
};

const formatDuration = (durationMs) => {
  if (durationMs == null) return '';
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds} giây`;
  return `${minutes} phút ${seconds.toString().padStart(2, '0')} giây`;
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'success':
      return { status: 'success', text: 'Thành công' };
    case 'fail':
      return { status: 'error', text: 'Thất bại' };
    case 'running':
      return { status: 'processing', text: 'Đang chạy' };
    case 'time_out':
      return { status: 'error', text: 'Timeout' };
    case 'not_implemented':
      return { status: 'warning', text: 'Chưa triển khai' };
    case 'not_start':
      return { status: 'error', text: 'Chưa chạy' };
    default:
      return { status: 'default', text: status || 'Không xác định' };
  }
};

const HistoryModal = ({ open, onClose, jobLogName, jobDisplayName }) => {
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState(null);

  const loadHistory = async () => {
    if (!jobLogName) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchCronJobHistory({ jobName: jobLogName, limit });
      if (!data?.status) {
        setError('Không thể tải lịch sử cron job.');
        setHistoryData(null);
      } else {
        setHistoryData(data?.data || null);
      }
    } catch (e) {
      setError('Có lỗi xảy ra khi tải lịch sử cron job.');
      setHistoryData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && jobLogName) {
      loadHistory();
    }
  }, [open, jobLogName, limit]);

  const runs = useMemo(() => historyData?.runs || [], [historyData]);

  const columns = [
    {
      title: 'Run ID',
      dataIndex: 'runId',
      key: 'runId',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const config = getStatusConfig(value);
        return <Badge status={config.status} text={config.text} />;
      },
    },
    {
      title: 'Bắt đầu',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Kết thúc',
      dataIndex: 'finished_at',
      key: 'finished_at',
      render: (value, record) => {
        if (!value && record.status === 'running') return 'Đang chạy';
        if (!value) return '';
        return formatDateTime(value);
      },
    },
    {
      title: 'Thời gian chạy',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value) => formatDuration(value),
    },
  ];

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={900}
      title={
        <div className="flex justify-between items-center w-full pr-10">
          <Typography.Title level={4} className="!mb-0">
            Lịch sử chạy: {jobDisplayName}
          </Typography.Title>
          <Space>
            <span>Giới hạn:</span>
            <Select
              value={limit}
              style={{ width: 90 }}
              options={LIMIT_OPTIONS.map((v) => ({ label: v, value: v }))}
              onChange={(value) => setLimit(value)}
            />
            <Button onClick={loadHistory} loading={loading} type="default">
              Reload
            </Button>
          </Space>
        </div>
      }
    >
      {error && (
        <div className="mb-3">
          <Alert type="error" message={error} showIcon />
        </div>
      )}

      {historyData && historyData.totalRuns === 0 && !loading ? (
        <Typography.Text>Chưa có lần chạy nào được ghi nhận.</Typography.Text>
      ) : (
        <Table
          rowKey={(record) => record.runId}
          loading={loading}
          dataSource={runs}
          columns={columns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      )}
    </Modal>
  );
};

export default HistoryModal;

